// types
import { DateRange } from '@mui/x-date-pickers-pro';
import {
  DateComputeRange,
  DateIsSingleOrRange,
  Period
} from './DateNavigation.types';

// utils
import { addDays, addMonths, addWeeks, addYears } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import {
  formatByLocale,
  getFourWeekInterval,
  getEightWeekInterval,
  getMonthInterval,
  getWeekInterval,
  getWorkweekInterval
} from 'utils/date';

export const toDate = (date: Dayjs | Date) =>
  dayjs.isDayjs(date) ? date.toDate() : date;

export const getFormatByPeriod = (
  date: Date | DateRange<Date | Dayjs>,
  period: Period | null
): string | undefined => {
  if (!period) return;

  const format = ({
    startDate,
    endDate
  }: {
    startDate: Date;
    endDate: Date;
  }) => {
    const differentMonth = startDate.getMonth() !== endDate.getMonth();
    const differentYear = startDate.getFullYear() !== endDate.getFullYear();

    const startMonthFormat = differentMonth ? 'MMM' : '';

    const startYearFormat = differentYear ? 'yyyy' : '';

    const endMonthFormat = differentMonth ? 'MMM' : 'MMMM';

    const endYearFormat = 'yyyy';

    return `${formatByLocale(
      startDate,
      `d ${startMonthFormat} ${startYearFormat}`.trim()
    )} - ${formatByLocale(
      endDate,
      `d ${endMonthFormat} ${endYearFormat}`.trim()
    )}`.replace(/\./g, '');
  };

  switch (period) {
    case 'day':
      return formatByLocale(date as Date, 'd MMMM yyyy');

    case 'week':
      return format(getWeekInterval(date as Date));

    case 'workweek':
      return format(getWorkweekInterval(date as Date));

    case 'month':
      return format(getMonthInterval(date as Date));

    case 'year':
      return dayjs(date as Date).format('YYYY');

    case 'fourWeeks':
      return format(getFourWeekInterval(date as Date));

    case 'eightWeeks':
      return format(getEightWeekInterval(date as Date));

    case 'custom': {
      const [start, end] = date as DateRange<Date | Dayjs>;
      if (!start && !end) return '';
      const startFormatted = dayjs(start).format('D MMMM YYYY');
      if (!end) return startFormatted;
      const endFormatted = dayjs(end).format('D MMMM YYYY');
      return startFormatted === endFormatted
        ? startFormatted
        : `${startFormatted} - ${endFormatted}`;
    }
  }
};

export const previousDate = (
  date: Date,
  period: Period,
  onPrevious?: (date: Date) => void
): void => {
  switch (period) {
    case 'day':
      return onPrevious?.(addDays(date, -1));
    case 'week':
    case 'workweek':
    case 'fourWeeks':
    case 'eightWeeks':
      return onPrevious?.(addWeeks(date, -1));
    case 'month':
      return onPrevious?.(addMonths(date, -1));
    case 'year':
      return onPrevious?.(addYears(date, -1));
  }
};

export const nextDate = (
  date: Date,
  period: Period,
  onNext?: (date: Date) => void
): void => {
  switch (period) {
    case 'day':
      return onNext?.(addDays(date, 1));
    case 'week':
    case 'workweek':
    case 'fourWeeks':
    case 'eightWeeks':
      return onNext?.(addWeeks(date, 1));
    case 'month':
      return onNext?.(addMonths(date, 1));
    case 'year':
      return onNext?.(addYears(date, 1));
  }
};

export function computeRange(
  selectedDate: DateIsSingleOrRange,
  period: Period | null
): DateComputeRange {
  if (period === 'custom') {
    if (!Array.isArray(selectedDate)) {
      return { startDate: '', endDate: '' };
    }
    const [start, end] = selectedDate;
    if (!start || !end) {
      return { startDate: '', endDate: '' };
    }
    return {
      startDate: dayjs(start).startOf('day').toISOString(),
      endDate: dayjs(end).endOf('day').toISOString()
    };
  }

  const singleDate: Date | Dayjs | string | number | null | undefined =
    Array.isArray(selectedDate) ? selectedDate[0] : selectedDate;

  const base = dayjs(singleDate);
  if (!base.isValid()) return { startDate: '', endDate: '' };

  if (period === 'workweek') {
    const start = base.isoWeekday
      ? base.isoWeekday(1).startOf('day')
      : base.startOf('week').add(0, 'day').startOf('day');
    const end = base.isoWeekday
      ? base.isoWeekday(5).endOf('day')
      : base.startOf('week').add(4, 'day').endOf('day');
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  }

  if (period === 'fourWeeks') {
    const start = base.isoWeekday
      ? base.isoWeekday(1).startOf('day')
      : base.startOf('week').startOf('day');
    const end = start.add(4, 'week').subtract(1, 'day').endOf('day');
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  }

  if (period === 'eightWeeks') {
    const start = base.isoWeekday
      ? base.isoWeekday(1).startOf('day')
      : base.startOf('week').startOf('day');
    const end = start.add(8, 'week').subtract(1, 'day').endOf('day');
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  }

  const unitByPeriod: Record<
    'day' | 'week' | 'month' | 'year',
    dayjs.OpUnitType
  > = {
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year'
  };

  const unit: dayjs.OpUnitType =
    unitByPeriod[(period ?? 'month') as keyof typeof unitByPeriod] ?? 'month';

  const start = base.startOf(unit);
  const end = base.endOf(unit);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString()
  };
}
